import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Table, 
    Button, 
    Space, 
    message, 
    Modal, 
    Form, 
    Input, 
    Select, 
    Switch,
    Tag,
    Typography,
    Popconfirm,
    Alert
} from 'antd';
import { 
    MailOutlined, 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { TextArea } = Input;

interface EmailTemplate {
    id: number;
    name: string;
    type: string;
    subject: string;
    body: string;
    variables?: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

const TEMPLATE_TYPES = [
    { value: 'welcome', label: 'Приветственное письмо' },
    { value: 'password_reset', label: 'Сброс пароля' },
    { value: 'tournament_registration', label: 'Регистрация на турнир' },
    { value: 'tournament_reminder', label: 'Напоминание о турнире' },
    { value: 'match_notification', label: 'Уведомление о матче' },
    { value: 'tournament_results', label: 'Результаты турнира' },
    { value: 'subscription_confirmation', label: 'Подтверждение подписки' },
    { value: 'subscription_expired', label: 'Истечение подписки' },
    { value: 'custom', label: 'Произвольный' }
];

// Доступные переменные для каждого типа шаблона
const TEMPLATE_VARIABLES: Record<string, Array<{ name: string; description: string }>> = {
    welcome: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'userFirstName', description: 'Имя' },
        { name: 'userLastName', description: 'Фамилия' }
    ],
    password_reset: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'resetLink', description: 'Ссылка для сброса пароля' },
        { name: 'resetToken', description: 'Токен для сброса пароля' },
        { name: 'expirationTime', description: 'Время истечения ссылки' }
    ],
    tournament_registration: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'tournamentName', description: 'Название турнира' },
        { name: 'tournamentDate', description: 'Дата турнира' },
        { name: 'tournamentLocation', description: 'Место проведения' },
        { name: 'categoryName', description: 'Название категории' },
        { name: 'registrationDate', description: 'Дата регистрации' }
    ],
    tournament_reminder: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'tournamentName', description: 'Название турнира' },
        { name: 'tournamentDate', description: 'Дата турнира' },
        { name: 'tournamentTime', description: 'Время начала' },
        { name: 'tournamentLocation', description: 'Место проведения' },
        { name: 'daysUntilTournament', description: 'Дней до турнира' }
    ],
    match_notification: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'tournamentName', description: 'Название турнира' },
        { name: 'matchNumber', description: 'Номер матча' },
        { name: 'matchTime', description: 'Время матча' },
        { name: 'opponentName', description: 'Имя соперника' },
        { name: 'categoryName', description: 'Название категории' },
        { name: 'tatamiNumber', description: 'Номер татами' }
    ],
    tournament_results: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'tournamentName', description: 'Название турнира' },
        { name: 'tournamentDate', description: 'Дата турнира' },
        { name: 'categoryName', description: 'Название категории' },
        { name: 'position', description: 'Занятое место' },
        { name: 'totalParticipants', description: 'Всего участников' }
    ],
    subscription_confirmation: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'planType', description: 'Тип подписки' },
        { name: 'startDate', description: 'Дата начала подписки' },
        { name: 'endDate', description: 'Дата окончания подписки' },
        { name: 'price', description: 'Стоимость подписки' },
        { name: 'paymentMethod', description: 'Способ оплаты' }
    ],
    subscription_expired: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'planType', description: 'Тип подписки' },
        { name: 'endDate', description: 'Дата окончания подписки' },
        { name: 'renewalLink', description: 'Ссылка для продления' }
    ],
    custom: [
        { name: 'userName', description: 'Имя пользователя' },
        { name: 'userEmail', description: 'Email пользователя' },
        { name: 'userFirstName', description: 'Имя' },
        { name: 'userLastName', description: 'Фамилия' }
    ]
};

const EmailTemplatesPage: React.FC = () => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
    const [form] = Form.useForm();
    const [selectedTemplateType, setSelectedTemplateType] = useState<string>('');

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response: any = await apiService.get('/email-templates');
            if (response.data?.data) {
                setTemplates(Array.isArray(response.data.data) ? response.data.data : []);
            }
        } catch (error: any) {
            logger.error('Error fetching email templates:', error);
            message.error('Не удалось загрузить шаблоны писем');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingTemplate(null);
        setSelectedTemplateType('');
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (template: EmailTemplate) => {
        setEditingTemplate(template);
        setSelectedTemplateType(template.type);
        form.setFieldsValue({
            ...template,
            variables: template.variables ? template.variables.join(', ') : ''
        });
        setModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await apiService.delete(`/email-templates/${id}`);
            message.success('Шаблон удален');
            fetchTemplates();
        } catch (error: any) {
            logger.error('Error deleting email template:', error);
            message.error('Не удалось удалить шаблон');
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            const data = {
                ...values,
                variables: values.variables 
                    ? values.variables.split(',').map((v: string) => v.trim()).filter((v: string) => v)
                    : []
            };

            if (editingTemplate) {
                await apiService.put(`/email-templates/${editingTemplate.id}`, data);
                message.success('Шаблон обновлен');
            } else {
                await apiService.post('/email-templates', data);
                message.success('Шаблон создан');
            }
            setModalVisible(false);
            setSelectedTemplateType('');
            form.resetFields();
            fetchTemplates();
        } catch (error: any) {
            logger.error('Error saving email template:', error);
            message.error(error.response?.data?.message || 'Не удалось сохранить шаблон');
        }
    };

    const columns: ColumnsType<EmailTemplate> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const typeOption = TEMPLATE_TYPES.find(t => t.value === type);
                return typeOption ? typeOption.label : type;
            }
        },
        {
            title: 'Тема',
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true
        },
        {
            title: 'Переменные',
            dataIndex: 'variables',
            key: 'variables',
            render: (variables: string[]) => {
                if (!variables || variables.length === 0) return '-';
                return (
                    <Space wrap>
                        {variables.map(v => (
                            <Tag key={v}>{v}</Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: 'Статус',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                isActive ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Активен</Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">Неактивен</Tag>
                )
            )
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить шаблон?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Title level={2}>
                <MailOutlined style={{ marginRight: 8 }} />
                Шаблоны писем
            </Title>

            <Card
                styles={{ body: { padding: '24px' } }}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Создать шаблон
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={templates}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Всего: ${total}`
                    }}
                />
            </Card>

            <Modal
                title={editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setSelectedTemplateType('');
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Название"
                        name="name"
                        rules={[{ required: true, message: 'Укажите название шаблона' }]}
                    >
                        <Input placeholder="Приветственное письмо" />
                    </Form.Item>

                    <Form.Item
                        label="Тип"
                        name="type"
                        rules={[{ required: true, message: 'Выберите тип шаблона' }]}
                    >
                        <Select 
                            options={TEMPLATE_TYPES}
                            onChange={(value) => setSelectedTemplateType(value)}
                        />
                    </Form.Item>

                    {selectedTemplateType && TEMPLATE_VARIABLES[selectedTemplateType] && (
                        <Alert
                            message="Доступные переменные для этого типа шаблона"
                            description={
                                <div style={{ marginTop: 8 }}>
                                    <Space wrap>
                                        {TEMPLATE_VARIABLES[selectedTemplateType].map((variable) => (
                                            <Tag key={variable.name} color="blue">
                                                <code>{'{{' + variable.name + '}}'}</code> - {variable.description}
                                            </Tag>
                                        ))}
                                    </Space>
                                    <div style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                                        <strong>Пример использования:</strong>
                                        <div style={{ marginTop: 4 }}>
                                            <code>
                                                {'<p>Здравствуйте, {{userName}}!</p>'}
                                            </code>
                                        </div>
                                    </div>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Form.Item
                        label="Тема письма"
                        name="subject"
                        rules={[{ required: true, message: 'Укажите тему письма' }]}
                    >
                        <Input placeholder="Добро пожаловать!" />
                    </Form.Item>

                    <Form.Item
                        label="Текст письма (HTML)"
                        name="body"
                        rules={[{ required: true, message: 'Укажите текст письма' }]}
                        extra="Используйте {{variableName}} для подстановки переменных"
                    >
                        <TextArea
                            rows={10}
                            placeholder="<h1>Добро пожаловать, {{userName}}!</h1><p>Ваш email: {{userEmail}}</p>"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Переменные (через запятую)"
                        name="variables"
                        extra="Список переменных, которые можно использовать в шаблоне"
                    >
                        <Input placeholder="userName, userEmail, tournamentName" />
                    </Form.Item>

                    <Form.Item
                        label="Активен"
                        name="isActive"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingTemplate ? 'Сохранить' : 'Создать'}
                            </Button>
                            <Button onClick={() => {
                                setModalVisible(false);
                                form.resetFields();
                            }}>
                                Отмена
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default EmailTemplatesPage;

